import React, { useCallback, useContext, useEffect } from 'react';
import { MyIpReturnHostContext } from '$/components/MyIpAssetReturnHost/context';
import MyIpAssetPanel from '$/pages/RoboneoMyIps/AssetPanel';
import type { MyIpItem } from '$/domains/myIp/types';
import { isMyIpCanvasLimitError } from '$/domains/myIp/myIpService';
import { notifyMyIpChanged } from '$/domains/myIp/changeEvents';
import { useMyIpSession } from '@/hooks/myIp/useMyIpSession';
import { useMyIpDetailLinks } from '@/hooks/myIp/useMyIpBackNavigation';
import { rememberMyIpReturnState } from '@/utils/myIp/backNavigation';
import { useMyIpLoginCreationAction } from '@/hooks/myIp/useMyIpLoginCreationAction';
import { useMyIpProjectAction } from '@/hooks/myIp/useMyIpProjectAction';
import { addMyIpToCurrentCanvas } from '$/pages/RoboneoTeamFlow/utils/myIpCanvas';
import { captureMyIpCanvasContext } from '$/pages/RoboneoTeamFlow/utils/myIpCanvasContext';
import { getLoadedTeamFlowIdentity } from '$/pages/RoboneoTeamFlow/utils/teamFlowLoadedIdentity';
import { isTeamFlowCanvasReadonly } from '$/pages/RoboneoTeamFlow/utils/isTeamFlowCanvasReadonly';
import { getMyIpItemMediaUrls, trackMyIpApplyCanvasClick } from '$/domains/myIp/tracking';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import {
  trackRoboneoAssetIpCardClick,
  trackRoboneoAssetIpCardShow,
  trackRoboneoAssetIpDetailClick,
} from '../tracking';

interface Props {
  assetType: 'personal' | 'official';
  requireCanvas?: boolean;
  trackRoomId?: string;
  onIpItemsAdd?: (items: MyIpItem[]) => void | Promise<void>;
  maxIpSelectableCount?: number;
  addLabel?: string;
  /** 隐藏详情素材页签；仅爆款复刻商品槽选择 IP 三视图时开启。 */
  hideMaterials?: boolean;
  /** 隐藏右栏详情，仅保留卡片列表与添加/批量。 */
  hideIpDetail?: boolean;
}

/** 分类切换重建私有数据面板，避免个人与官方资产的选中状态互相复用。 */
export default function MyIpAssetContent({
  assetType,
  requireCanvas = false,
  trackRoomId,
  onIpItemsAdd,
  maxIpSelectableCount,
  addLabel,
  hideMaterials,
  hideIpDetail,
}: Props) {
  return (
    <AssetContent
      key={assetType}
      assetType={assetType}
      requireCanvas={requireCanvas}
      trackRoomId={trackRoomId}
      onIpItemsAdd={onIpItemsAdd}
      maxIpSelectableCount={maxIpSelectableCount}
      addLabel={addLabel}
      hideMaterials={hideMaterials}
      hideIpDetail={hideIpDetail}
    />
  );
}

/** 应用提供登录、项目创建与画布落点动作，资产面板只负责呈现和转发。 */
function AssetContent({
  assetType,
  requireCanvas,
  trackRoomId = '',
  onIpItemsAdd,
  maxIpSelectableCount,
  addLabel,
  hideMaterials,
  hideIpDetail,
}: Props) {
  const session = useMyIpSession();
  // 资产库个人角色入口复用“我的 IP”页的登录、创建与失败重试链路。
  const { create, disabled, failed } = useMyIpLoginCreationAction({ requireCanvas });
  const host = useContext(MyIpReturnHostContext);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const createProjectWithIp = useMyIpProjectAction(session.scopeKey, session.paths.prefix);
  const initialState = host?.restored?.scopeKey === session.scopeKey && host.restored.state.assetType === assetType
    ? host.restored.state : undefined;
  /** 子面板已接收恢复快照后即清除宿主交接，切换分类或重新打开不能重复恢复。 */
  useEffect(() => { if (initialState) host?.onRestored?.(); }, [initialState, host]);
  const getDetailHref = useMyIpDetailLinks(session.scopeKey, session.paths.list);
  /** 上报角色 IP 卡片曝光，官方/个人事件由埋点适配层按资产身份分流。 */
  const handleIpCardShow = useCallback((item: MyIpItem, positionId: number, keyword: string) => {
    trackRoboneoAssetIpCardShow({
      item, roomId: trackRoomId, keyword, positionId,
    });
  }, [trackRoomId]);
  /** 上报角色 IP 卡片点击，保持卡片位置和当前搜索词与曝光口径一致。 */
  const handleIpCardClick = useCallback((item: MyIpItem, positionId: number, keyword: string) => {
    trackRoboneoAssetIpCardClick({
      item, roomId: trackRoomId, keyword, positionId,
    });
  }, [trackRoomId]);
  /** 上报角色 IP 详情入口点击，随后继续执行宿主返回状态记录。 */
  const handleIpDetailClick = useCallback((item: MyIpItem) => {
    trackRoboneoAssetIpDetailClick(item);
  }, []);
  /** Team Studio 直接落点 IP 组；首页从 IP 创建项目后进入画布，两个入口共用设计稿操作位。 */
  const handleApply = useCallback(async (item: MyIpItem) => {
    if (!requireCanvas) {
      await createProjectWithIp(item);
      return;
    }
    const isCurrent = captureMyIpCanvasContext();
    const identity = getLoadedTeamFlowIdentity();
    if (!identity || isTeamFlowCanvasReadonly() || !isCurrent()) {
      throw new Error('IP canvas is unavailable');
    }
    trackMyIpApplyCanvasClick({
      roomId: identity.roomId,
      projectId: identity.projectId,
      canvasId: identity.canvasId,
      ipId: item.ipId,
    }, getMyIpItemMediaUrls(item), item.assetType === 'official' ? 'asset_official_ip_apply' : 'asset_personal_ip_apply');
    try {
      await addMyIpToCurrentCanvas(item, false, isCurrent, { nodeSource: 'asset_ip_click' });
      // 画布关联成功后同步项目 IP 头像列表，失败时不广播绑定变更。
      notifyMyIpChanged();
      message.success(languageStore.smartcatTranslationText((value) => value.asset_applied_to_canvas));
    } catch (error) {
      if (!isMyIpCanvasLimitError(error)) {
        message.error(languageStore.smartcatTranslationText((value) => value.roboneo_failed_try_again));
      }
      throw error;
    }
  }, [createProjectWithIp, languageStore, requireCanvas]);
  /** 选择器右栏复用单张添加回调；画布场景继续走原有 IP 组落点。 */
  const handlePanelAction = useCallback(async (item: MyIpItem) => {
    if (onIpItemsAdd) {
      await onIpItemsAdd([item]);
      return;
    }
    await handleApply(item);
  }, [handleApply, onIpItemsAdd]);
  return (
    <MyIpAssetPanel
      key={`${session.scopeKey}:${assetType}`}
      scopeKey={session.scopeKey}
      enabled={session.enabled}
      assetType={assetType}
      initialState={initialState}
      onCreate={assetType === 'personal' ? create : undefined}
      createDisabled={disabled}
      creationFailed={failed}
      onTrackCardShow={handleIpCardShow}
      onTrackCardClick={handleIpCardClick}
      onTrackIpDetailClick={handleIpDetailClick}
      onApplyToCanvas={handlePanelAction}
      applyToCanvasDisabled={requireCanvas && isTeamFlowCanvasReadonly()}
      onAdd={onIpItemsAdd}
      maxSelectableCount={maxIpSelectableCount}
      addLabel={addLabel}
      hideMaterials={hideMaterials}
      hideIpDetail={hideIpDetail}
      onViewSettings={host ? (item, state) => rememberMyIpReturnState(getDetailHref(item.ipId), session.scopeKey, {
        ...state, kind: 'asset', host: host.host, assetType,
      }) : undefined}
      getDetailHref={(item) => getDetailHref(item.ipId)}
    />
  );
}
